// This is the one allowed import of useAtom and friends in FileViewer, to power our useAtom derivatives.
// eslint-disable-next-line no-restricted-imports
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useAtomCallback } from 'jotai/utils';
/**
 * @returns [value, setValue] for a given FileViewerAtom. A FileViewerAtom is an internal wrapper around a jotai atom.
 *  Its value should be the same across individual files in a single FileViewer. The main difference between this and
 * jotai's useAtom is that this is limited to writable atoms only. For Read only atoms, use useFileViewerAtomValue to get the value
 */
export const useFileViewerAtom = (fvAtom) => {
    return useAtom(fvAtom);
};
/**
 * @returns The value of a FileViewerAtom for the current File Viewer session. Unlike useFileViewerAtom, this hook
 * does not provide a setter. This can optimize re-renders when the setter is not needed.
 */
export const useFileViewerAtomValue = (fvAtom) => {
    return useAtomValue(fvAtom);
};
/**
 * @returns A setter for a given FileViewerAtom. This setter will update the value for the current File Viewer session.
 * Unlike useFileViewerAtom, this hook does not provide the current value. This can optimize re-renders when the
 * current value is not needed.
 */
export const useSetFileViewerAtom = (fvAtom) => {
    return useSetAtom(fvAtom);
};
/**
 * @returns [value, setValue] for a given PreviewKeyAtom. A PreviewKeyAtom is an atom keyed by
 *   PreviewKey. Its value should be meaningfully different across individual files.
 */
export const usePreviewKeyAtom = (aPreviewKeyAtom, previewKey) => {
    return useFileViewerAtom(aPreviewKeyAtom(previewKey));
};
/**
 * @returns The value of a PreviewKeyAtom for a given previewKey.
 * @see usePreviewKeyAtom
 */
export const usePreviewKeyAtomValue = (aPreviewKeyAtom, previewKey) => {
    return useFileViewerAtomValue(aPreviewKeyAtom(previewKey));
};
/**
 * @returns A callback returning the value of a PreviewKeyAtom for a given previewKey.
 * @see usePreviewKeyAtom
 */
export const usePreviewKeyAtomCallback = (aPreviewKeyAtom, previewKey) => {
    return useAtomCallback(useCallback((get) => {
        var _a;
        return (_a = get(aPreviewKeyAtom(previewKey))) !== null && _a !== void 0 ? _a : 0;
    }, [aPreviewKeyAtom, previewKey]));
};
/**
 * @returns A callback to set the value of a PreviewKeyAtom for a given previewKey.
 * @see usePreviewKeyAtom
 */
export const useSetPreviewKeyAtom = (aPreviewKeyAtom, previewKey) => {
    return useSetFileViewerAtom(aPreviewKeyAtom(previewKey));
};
//# sourceMappingURL=use_atom.preenvesnextjs.map
